import { Add, ChevronLeft, ChevronRight, PhotoAlbumOutlined, WhatsApp } from '@mui/icons-material'
import { Box, Button, LinearProgress, List, Slide, Stack, Typography } from '@mui/material'
import MuiDrawer from '@mui/material/Drawer'
import { DebouncedFuncLeading } from 'lodash'
import { memo } from 'react'
import ReactInfiniteScroll from 'react-infinite-scroll-component'
import { Link } from 'react-router-dom'
import AutoSizer from 'react-virtualized-auto-sizer'
import theme from '../../../../../config/theme'
import { Bucket, BucketAlbum as BucketAlbumGraphType } from '../../../../../graphql/generated'
import AccountMenu from '../components/AccountMenu'
import BucketAlbum from '../components/Album'
import AlbumSubHeader from '../components/AlbumSubHeader'
import BucketSwitcher from '../components/BucketSwitcher'
import StaticAlbums from '../components/StaticAlbums'

interface Props {
  bucketId: string
  userId: string
  drawerSection: 'albums' | 'main'
  bucket: Bucket | null | undefined
  showWhatsAppButton: boolean | null | undefined
  loading: boolean
  hasActiveWhatsAppIntegration: boolean | undefined
  handleOpenAlbums: () => void
  handleBack: () => void
  createBucket: () => void
  loadingMore: boolean
  freemiumUserReachedMaxBuckets: boolean
  canCreateBucket: boolean
  onClickWhatsApp: () => void
  albums: BucketAlbumGraphType[]
  nextToken: string | null | undefined
  fetchMore: DebouncedFuncLeading<() => void>
}

function DesktopDrawer({
  bucketId,
  userId,
  loading,
  loadingMore,
  albums,
  showWhatsAppButton,
  bucket,
  canCreateBucket,
  fetchMore,
  nextToken,
  drawerSection,
  hasActiveWhatsAppIntegration,
  freemiumUserReachedMaxBuckets,
  createBucket,
  handleBack,
  handleOpenAlbums,
  onClickWhatsApp
}: Readonly<Props>) {
  const AlbumSection = (
    <Box
      sx={{
        flex: 1,
        position: 'relative',
        overflowX: 'hidden',
        width: '260px',
        height: '100%',
        padding: '8px'
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <AutoSizer>
          {({ height, width }) => (
            <div style={{ height, width, position: 'relative' }} id="scrollable-gallery">
              {loading && <LinearProgress />}
              <List sx={{ paddingTop: 0, paddingBottom: 0, flex: 'auto', padding: '8px' }}>
                <ReactInfiniteScroll
                  dataLength={albums.length || 0}
                  loader={
                    <Typography variant="h6" align="center">
                      Loading...
                    </Typography>
                  }
                  next={fetchMore}
                  hasMore={Boolean(nextToken)}
                >
                  {!loading && <AlbumSubHeader bucketId={bucketId} />}
                  {albums.map((album) => (
                    <BucketAlbum album={album} key={album.id} bucketId={bucketId} />
                  ))}
                  {Boolean(nextToken) && (
                    <Button disabled={loadingMore} onClick={fetchMore} fullWidth>
                      load more
                    </Button>
                  )}
                  <Box>
                    <StaticAlbums />
                  </Box>
                </ReactInfiniteScroll>
              </List>
            </div>
          )}
        </AutoSizer>
      </Box>
    </Box>
  )

  return (
    <MuiDrawer
      sx={{
        position: 'relative',
        width: 280,
        flexShrink: 0,
        height: '100%',
        [`& .MuiDrawer-paper`]: {
          position: 'static',
          width: '280px',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }
      }}
      variant="permanent"
      PaperProps={{ id: 'album-menu' }}
    >
      <Box className="switcher-header" sx={{ flex: 0, minHeight: '40px', padding: '8px' }}>
        {drawerSection === 'main' ? (
          <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography component="h3" sx={{ fontSize: '1.5rem', fontWeight: '800', lineHeight: 1.25 }}>
              Buckets
            </Typography>
            <Box>
              <Button
                disabled={!canCreateBucket || freemiumUserReachedMaxBuckets}
                variant="contained"
                color="primary"
                onClick={createBucket}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '4px',
                  borderRadius: '20px',
                  boxShadow: 'none',
                  minWidth: 'auto',
                  padding: '4px 12px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  height: '24px',
                  '& .MuiButton-startIcon': {
                    marginRight: 0
                  }
                }}
                startIcon={<Add sx={{ fontSize: '16px' }} />}
              >
                Bucket
              </Button>
            </Box>
          </Box>
        ) : (
          <Button
            onClick={handleBack}
            startIcon={<ChevronLeft />}
            sx={{
              padding: 0,
              minWidth: 0,
              color: theme.palette.text.primary,
              ':disabled': { color: theme.palette.text.primary },
              ':hover': { backgroundColor: 'transparent', color: theme.palette.primary.main }
            }}
          >
            <Typography component="h3" sx={{ fontSize: '0.875rem', fontWeight: '600', lineHeight: 1.25 }}>
              Back
            </Typography>
          </Button>
        )}
      </Box>
      <Box height="100%" sx={{ flex: 1, position: 'relative' }}>
        <Box sx={{ height: '100%' }}>
          <Stack height="100%" direction={drawerSection === 'main' ? 'column' : 'row'} sx={{ gap: '8px' }}>
            <Slide direction="right" in={drawerSection === 'main'} mountOnEnter unmountOnExit>
              <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', padding: '8px 8px 0px' }}>
                <Box sx={{ flex: '1 0 0', maxHeight: 'fit-content', minHeight: 0, overflowY: 'auto' }}>
                  <Box>
                    <BucketSwitcher userId={userId} drawerSection={drawerSection} />
                  </Box>

                  <Box sx={{ paddingRight: '8px' }}>
                    <Link
                      to="/buckets"
                      style={{
                        textDecoration: 'none',
                        color: theme.palette.secondary.main,
                        fontSize: '.875rem',
                        fontWeight: 500,
                        display: 'block',
                        textAlign: 'right',
                        padding: '4px 0'
                      }}
                    >
                      View All
                    </Link>
                  </Box>
                </Box>

                {!bucket?.isBanned && (
                  <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Button
                      onClick={handleOpenAlbums}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        color: theme.palette.text.primary,
                        padding: '8px 0',
                        fontSize: '1.125rem'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <PhotoAlbumOutlined />
                        {albums.length} Albums
                      </Box>
                      <ChevronRight />
                    </Button>
                  </Box>
                )}
              </Box>
            </Slide>
            <Slide direction="left" in={drawerSection === 'albums'} mountOnEnter unmountOnExit>
              {AlbumSection}
            </Slide>
          </Stack>
        </Box>
      </Box>
      <Box
        className="lower-buttons"
        sx={{
          width: '280px',
          backgroundColor: 'white',
          borderTop: `1px solid ${theme.palette.divider}`,
          minHeight: showWhatsAppButton ? '95px' : '50px'
        }}
      >
        {showWhatsAppButton && (
          <Box sx={{ pt: '4px' }}>
            <Button
              fullWidth
              variant="text"
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                alignContent: 'center',
                color: '#101828',
                gap: '4px',
                padding: '8px',
                borderBottom: `1px solid ${theme.palette.customGrey[200]}`,
                '.MuiListItem-root': {
                  padding: 0
                },
                '&:hover': {
                  background: 'transparent',
                  color: theme.palette.primary.main
                }
              }}
              onClick={onClickWhatsApp}
            >
              <WhatsApp />
              <Typography sx={{ fontWeight: '500', fontSize: '16px', alignContent: 'center' }}>
                {hasActiveWhatsAppIntegration ? 'Manage WhatsApp' : 'Connect to WhatsApp'}
              </Typography>
              {!hasActiveWhatsAppIntegration && (
                <Typography
                  component="span"
                  sx={{ backgroundColor: '#35E8B1', borderRadius: '1px', padding: '1px 4px', fontWeight: '700', fontSize: '10px', ml: '4px' }}
                >
                  new
                </Typography>
              )}
            </Button>
          </Box>
        )}
        <AccountMenu />
      </Box>
    </MuiDrawer>
  )
}

export default memo(DesktopDrawer)
